import Head from 'expo-router/head';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { ArticleCard } from '@/components/riviera/article-card';
import { MoreLink, SectionHeading, landingStyles } from '@/components/riviera/landing-parts';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useArticles } from '@todo/core/hooks/use-articles';
import { useTheme } from '@/hooks/use-theme';
import { cities } from '@/lib/events-data';
import { cityPageCopy } from '@/lib/i18n/city-content';
import { articlesHubText } from '@/lib/i18n/hub-content';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { winePageCopy } from '@/lib/i18n/wine-content';
import { articlesIndexSeedKey } from '@todo/core/list-queries';
import { urlForImage } from '@todo/core/sanity/client';
import type { Article } from '@todo/core/sanity/queries';
import { absoluteUrl, breadcrumbJsonLd, hreflangLinks } from '@todo/core/seo';

// The articles hub. It exists for the same reason app/[lang]/events/index.tsx
// does: it's the crawlable index for every Sanity `post` detail page per
// locale. Those detail pages were already statically exported (see
// generateArticleStaticParams), but nothing in the static HTML linked to
// them — the homepage's carousel is client-rendered only — so they had no
// inbound links at all. Mirrors app/[lang]/tours/index.tsx and
// app/[lang]/events/index.tsx deliberately, minus the city chips: articles
// aren't geo-tagged.

// schema.org/ItemList — marks this as a hub page and hands Google the member
// URLs directly, independent of whether it renders the page's JS.
function articlesItemListJsonLd(articles: Article[], locale: Locale, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url,
    numberOfItems: articles.length,
    itemListElement: articles.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: article.title,
      url: absoluteUrl(locale, `articles/${article.slug}`),
    })),
  };
}

export default function ArticlesIndexScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { articles, loading, error } = useArticles(locale);

  const canonical = absoluteUrl(locale, 'articles');
  // Represents the hub with its most recent article's photo rather than a
  // generic brand image — real, current content beats a static placeholder.
  const ogImage = articles[0]?.mainImage
    ? urlForImage(articles[0].mainImage).width(1200).height(630).url()
    : null;

  // Hand-written, evergreen SEO copy — see hub-content.ts.
  const copy = articlesHubText(locale);
  // The guide hub links on to every city guide ("Things to Do in Nice") and to
  // the catalogue hubs, with their own titles as anchor text. All of these are
  // always exported (the city guides for every city, the hubs from the nav).
  const cityGuides = cities.flatMap((city) => {
    const guide = cityPageCopy(city.id, locale);
    return guide ? [{ id: city.id, label: guide.title }] : [];
  });
  const wineTitle = winePageCopy(locale)?.title;

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{t('seo.articlesTitle')}</title>
        <meta name="description" content={t('seo.articlesDescription')} />
        <link rel="canonical" href={canonical} />
        {hreflangLinks('articles').map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('seo.articlesTitle')} />
        <meta property="og:description" content={t('seo.articlesDescription')} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={t('seo.articlesTitle')} />
        <meta name="twitter:description" content={t('seo.articlesDescription')} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [{ name: t('nav.articles') }]))}
        </script>
        {articles.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(articlesItemListJsonLd(articles, locale, canonical))}
          </script>
        ) : null}
        {/* Baked rows so the client's first render matches this static HTML. */}
        {listSeedScript(articlesIndexSeedKey(locale))}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {t('articles.headingRegion')}
            </Text>
            {copy.intro.map((paragraph, index) => (
              <Text key={index} style={[landingStyles.intro, { color: theme.text }]}>
                {paragraph}
              </Text>
            ))}

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('articles.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error.message}</Text>
            ) : articles.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('articles.empty')}
              </Text>
            ) : (
              <View style={styles.grid}>
                {articles.map((article) => (
                  <ArticleCard key={article._id} article={article} locale={locale} />
                ))}
              </View>
            )}

            {cityGuides.length > 0 ? (
              <View style={landingStyles.section}>
                <SectionHeading>{copy.cityGuidesHeading}</SectionHeading>
                {cityGuides.map((guide) => (
                  <MoreLink
                    key={guide.id}
                    label={guide.label}
                    href={{ pathname: '/[lang]/[city]', params: { lang: locale, city: guide.id } }}
                  />
                ))}
              </View>
            ) : null}

            <View style={landingStyles.section}>
              <SectionHeading>{t('articles.planHeading')}</SectionHeading>
              <MoreLink
                label={t('events.headingRegion')}
                href={{ pathname: '/[lang]/events', params: { lang: locale } }}
              />
              <MoreLink
                label={t('tours.headingRegion')}
                href={{ pathname: '/[lang]/tours', params: { lang: locale } }}
              />
              {wineTitle ? (
                <MoreLink
                  label={wineTitle}
                  href={{ pathname: '/[lang]/wine-tours', params: { lang: locale } }}
                />
              ) : null}
              <MoreLink
                label={t('boats.headingRegion')}
                href={{ pathname: '/[lang]/boats', params: { lang: locale } }}
              />
            </View>
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  status: {
    fontSize: 14,
    paddingVertical: Spacing.four,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
});
